import type {
  IListaSqlFila,
  IListaSqlResultado,
  IListaSqlResultadoGrafica,
  SizeModalListaSql,
  TipoGraficaListaSql,
  TipoVisualizacionListaSql
} from '@/modules/Configuracion/interfaces'
import type { IFormComponent } from '@/modules/Configuracion/interfaces/IFormComponent'

const COLUMNAS_METADATA = ['TITULO', 'SIZE_MODAL']
const SIZES_MODAL: SizeModalListaSql[] = ['sm', 'md', 'lg', 'xl']
const crearErrorFormatoListaSql = (mensaje: string): Error => {
  const error = new Error(mensaje)
  error.name = 'ErrorFormatoListaSql'
  return error
}
const VISUALIZACIONES_LISTA_SQL_VALIDAS: TipoVisualizacionListaSql[] = [
  'BOTON_MODAL',
  'TABLA_MODAL',
  'GRAFICA_BARRAS',
  'BOTON_MODAL_GRAFICA_BARRAS',
  'GRAFICA_CIRCULAR',
  'BOTON_MODAL_GRAFICA_CIRCULAR'
]
const VISUALIZACIONES_LISTA_SQL_DIRECTAS: TipoVisualizacionListaSql[] = [
  'TABLA_MODAL',
  'GRAFICA_BARRAS',
  'GRAFICA_CIRCULAR'
]
export const VISUALIZACION_LISTA_SQL_DEFAULT: TipoVisualizacionListaSql = 'BOTON_MODAL'
export const VISUALIZACIONES_LISTA_SQL: Array<{
  text: string
  val: TipoVisualizacionListaSql
}> = [
  { text: 'Button/Modal', val: 'BOTON_MODAL' },
  { text: 'Tabla/Modal', val: 'TABLA_MODAL' },
  { text: 'Gráfica de barras', val: 'GRAFICA_BARRAS' },
  { text: 'Button/Modal - Gráfica de barras', val: 'BOTON_MODAL_GRAFICA_BARRAS' },
  { text: 'Gráfica circular', val: 'GRAFICA_CIRCULAR' },
  { text: 'Button/Modal - Gráfica circular', val: 'BOTON_MODAL_GRAFICA_CIRCULAR' }
]

export const resolverVisualizacionListaSql = (
  visualizacion?: string | null
): TipoVisualizacionListaSql => {
  return VISUALIZACIONES_LISTA_SQL_VALIDAS.includes(visualizacion as TipoVisualizacionListaSql)
    ? visualizacion as TipoVisualizacionListaSql
    : VISUALIZACION_LISTA_SQL_DEFAULT
}

export const esVisualizacionListaSqlDirecta = (
  visualizacion?: string | null
): boolean => {
  return VISUALIZACIONES_LISTA_SQL_DIRECTAS.includes(
    resolverVisualizacionListaSql(visualizacion)
  )
}

export const obtenerTituloFallbackListaSql = (componente: IFormComponent): string => {
  return componente.properties?.coreNombre || componente.label || 'Consulta SQL'
}

export const tieneReporteListaSql = (componentes: IFormComponent[] = []): boolean => {
  return componentes.every(componente => {
    if (componente.typeComponent === 'LISTA_SQL' && !componente.properties?.coreId) {
      return false
    }
    return tieneReporteListaSql(componente.components)
  })
}

export const normalizarResultadoListaSql = (
  resultado: unknown[],
  label: string
): IListaSqlResultado => {
  const filasOriginales = resultado.filter(
    (fila): fila is IListaSqlFila => typeof fila === 'object' && fila !== null && !Array.isArray(fila)
  )
  const metadata = filasOriginales[0] ?? {}
  const sizeRecibido = String(metadata.SIZE_MODAL ?? '').toLowerCase() as SizeModalListaSql
  const columnas = filasOriginales.length > 0
    ? Object.keys(metadata).filter(columna => !COLUMNAS_METADATA.includes(columna))
    : []
  const filas = filasOriginales.map(fila => {
    return Object.fromEntries(
      Object.entries(fila).filter(([columna]) => !COLUMNAS_METADATA.includes(columna))
    )
  })

  return {
    titulo: String(metadata.TITULO ?? '').trim() || label,
    sizeModal: SIZES_MODAL.includes(sizeRecibido) ? sizeRecibido : 'md',
    columnas,
    filas
  }
}

export const normalizarResultadoGraficaListaSql = (
  resultado: unknown[],
  label: string,
  tipoGrafica: TipoGraficaListaSql
): IListaSqlResultadoGrafica => {
  const filas = resultado.filter(
    (fila): fila is IListaSqlFila => typeof fila === 'object' && fila !== null && !Array.isArray(fila)
  )
  const metadata = filas[0] ?? {}
  const sizeRecibido = String(metadata.SIZE_MODAL ?? '').toLowerCase() as SizeModalListaSql

  if (filas.length === 0) {
    return {
      titulo: label,
      sizeModal: 'md',
      puntos: []
    }
  }

  if (!Object.prototype.hasOwnProperty.call(metadata, 'LABEL')) {
    throw crearErrorFormatoListaSql('El reporte debe devolver la columna LABEL para construir la gráfica.')
  }
  if (!Object.prototype.hasOwnProperty.call(metadata, 'VALUE')) {
    throw crearErrorFormatoListaSql('El reporte debe devolver la columna VALUE para construir la gráfica.')
  }

  const puntos = filas.map((fila, indice) => {
    const puntoLabel = String(fila.LABEL ?? '').trim()
    if (!puntoLabel) {
      throw crearErrorFormatoListaSql(`La fila ${indice + 1} debe tener un LABEL válido.`)
    }

    const valorOriginal = fila.VALUE
    if (valorOriginal === null || valorOriginal === undefined || valorOriginal === '') {
      throw crearErrorFormatoListaSql(`La fila ${indice + 1} debe tener un VALUE numérico.`)
    }
    const value = Number(valorOriginal)
    if (!Number.isFinite(value)) {
      throw crearErrorFormatoListaSql(`La fila ${indice + 1} debe tener un VALUE numérico.`)
    }
    if (tipoGrafica === 'doughnut' && value < 0) {
      throw crearErrorFormatoListaSql('La gráfica circular no admite valores negativos en VALUE.')
    }

    return { label: puntoLabel, value }
  })

  if (tipoGrafica === 'doughnut' && puntos.every(punto => punto.value === 0)) {
    throw crearErrorFormatoListaSql('La gráfica circular necesita al menos un VALUE mayor que cero.')
  }

  return {
    titulo: String(metadata.TITULO ?? '').trim() || label,
    sizeModal: SIZES_MODAL.includes(sizeRecibido) ? sizeRecibido : 'md',
    puntos
  }
}
